import React, { useEffect, useState, useMemo } from "react";
import toDateTimeLocal, {
  toDateLocal,
  parseToLocalFixedHour,
} from "../../../utils/toDateTimeLocal";
import { Modal, Button, Form } from "react-bootstrap";
import PMChart from "./PMChart";
import DatePicker from "../../layouts/DatePicker";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  loadPMResultsList,
  addPMGoal,
  updatePMGoal,
} from "../../../actions/admin";
import _, { add } from "lodash";

// Compute a global X-axis domain across all PM results so charts align
const PLOTTING_RANGE_DAYS = 365;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const PMDashboard = ({
  loadPMResultsList,
  addPMGoal,
  updatePMGoal,
  pmresultsList,
  pmresultsListLoading,
  user,
  selectedUser,
  schoolYears,
}) => {
  const [goalSettings, setGoalSettings] = useState(null);
  const [goalForm, setGoalForm] = useState({
    goalDesc: "",
    startDate: "",
    endDate: "",
    baseScore: "",
    targetScore: "",
  });

  // Hardcoded target scores mapped by LevelID
  // End of Year Target Scores mapped by CategoryID and Level from Norms
  const ACADIENCE_EOY_TARGETS = {
    // Category 3: Silly Words
    3: {
      CLS: { 1: 58, 2: 54 },
      WWR: { 1: 13, 2: 13 },
    },
    // Category 4: Story Reading
    4: {
      "%": { 1: 90, 2: 97, 3: 97, 4: 98, 5: 99, 6: 98, 7: 96, 8: 96 },
      TWC: { 1: 47, 2: 87, 3: 100, 4: 115, 5: 130, 6: 120, 7: 141, 8: 135 },
    },
    // Catch-all fallbacks for other letter/sound categories (Category 1, 2, etc.)
    "Letter Sounds": { 1: 35, 2: 44 },
    "Name the Letter": { 1: 42, 2: 59 },
    "Break Up The word": { 1: 40, 2: 40 },
    "Say The First Sound": { 1: 30, 2: 30 },
  };

  const handleGoalChange = (field, value) => {
    setGoalForm((g) => ({ ...g, [field]: value }));
  };

  const handleSaveGoal = async () => {
    const selectedGroup =
      goalSettings?.data || groupedData[goalSettings?.groupKey];

    const selectedAssessmentResultsID = selectedGroup.map((d) => d.ResultID);
    // build submission object so state update doesn't cause stale payload
    const submission = {
      ...goalForm,
      resultID: selectedAssessmentResultsID,
    };

    // update local form state and send submission to API
    setGoalForm(submission);
    try {
      if (goalExisting && goalExisting.GoalID) {
        await updatePMGoal(goalExisting.GoalID, submission);
      } else {
        await addPMGoal(submission);
      }

      // reload PM results so the charts reflect the new/updated goal
      if (selectedUser && selectedUser.UserID) {
        await loadPMResultsList(selectedUser.UserID);
      }

      setGoalSettings(null);
      setGoalForm({
        goalDesc: "",
        startDate: "",
        endDate: "",
        baseScore: "",
        targetScore: "",
      });
    } catch (err) {}
  };

  const filteredPmResults = useMemo(() => {
    if (!pmresultsList || pmresultsList.length === 0) return [];
    return pmresultsList.filter(
      (item) => !item.AccessCode || String(item.AccessCode).trim() === "",
    );
  }, [pmresultsList]);

  // grouped data by LevelID_CategoryID
  const groupedData = useMemo(() => {
    if (!filteredPmResults || filteredPmResults.length === 0) return {};

    // For CategoryID 3 and 4 (Chart Story Reading / SillyWord combos), split by skill
    // (eg CLS vs WWR, TWC vs %). For other categories, group only by LevelID_CategoryID.
    return _.groupBy(filteredPmResults, (item) => {
      const cat = item.CategoryID;
      let skill = "";
      try {
        if (item.ResultRef) {
          const parsed =
            typeof item.ResultRef === "string"
              ? JSON.parse(item.ResultRef)
              : item.ResultRef;
          skill = parsed?.AssessmentSkill || parsed?.Skill || "";
        }
      } catch (err) {
        // ignore parse errors
      }
      skill = skill || item.AssessmentSkill || item.SkillType || "";

      if (cat === 3 || cat === 4) {
        // create distinct groups per skill for these categories
        const skillKey = skill || "unknown";
        return `${item.LevelID}_${item.CategoryID}_${skillKey}`;
      }

      // default grouping: one chart per category per level
      return `${item.LevelID}_${item.CategoryID}`;
    });
  }, [filteredPmResults]);

  const globalChartDomain = useMemo(() => {
    if (!filteredPmResults || filteredPmResults.length === 0) return null;
    const times = [];
    filteredPmResults.forEach((item) => {
      if (item.CreatedDate) times.push(new Date(item.CreatedDate).getTime());
      if (item.StartDate) times.push(parseToLocalFixedHour(item.StartDate, 19));
      if (item.EndDate) times.push(parseToLocalFixedHour(item.EndDate, 19));
      if (item.InterventionDate)
        times.push(parseToLocalFixedHour(item.InterventionDate, 19));
    });
    const valid = times.filter((t) => typeof t === "number" && !isNaN(t));
    if (!valid || valid.length === 0) return null;
    // align domain to the school year that contains the latest data point
    const maxT = Math.max(...valid);
    const maxDate = new Date(maxT);
    // JS months: 0 = Jan, 7 = Aug
    const startYear =
      maxDate.getMonth() >= 7
        ? maxDate.getFullYear()
        : maxDate.getFullYear() - 1;
    const schoolStart = new Date(startYear, 7, 1, 0, 0, 0, 0).getTime(); // Aug 1
    const schoolEnd = new Date(startYear + 1, 6, 31, 23, 59, 59, 999).getTime(); // Jul 31 next year
    return [schoolStart, schoolEnd];
  }, [filteredPmResults]);

  const goalExisting = useMemo(() => {
    if (!groupedData || !goalSettings?.groupKey) return null;
    const group = groupedData[goalSettings.groupKey];

    if (!group || group.length === 0) return null;
    // Use .find() instead of [0] so a newly added result (no GoalID) appearing
    // first in the array doesn't hide the existing goal.
    const goalInfo = group.find((r) => r.GoalID);
    if (!goalInfo || !goalInfo.GoalID) return null;

    const firstResult = group[0];
    return {
      ResultID: goalInfo.ResultID,
      GoalID: goalInfo.GoalID,
      GoalDesc: goalInfo.GoalDesc,
      StartDate: toDateLocal(goalInfo.StartDate),
      EndDate: toDateLocal(goalInfo.EndDate),
      BaseScore: firstResult?.TotalScore ?? goalInfo.BaseScore,
      TargetScore: goalInfo.TargetScore,
    };
  }, [groupedData && goalSettings]);

  // Automatically provision goal lines silently for groups missing them
  useEffect(() => {
    if (
      pmresultsListLoading ||
      !groupedData ||
      Object.keys(groupedData).length === 0
    )
      return;

    const provisionMissingGoals = async () => {
      let structureUpdated = false;

      for (const key of Object.keys(groupedData)) {
        const group = groupedData[key] || [];
        if (group.length === 0) continue;

        // Check if any record in this specific group already possesses a GoalID
        const hasExistingGoal = group.some((item) => item.GoalID);
        if (hasExistingGoal) continue; // Skip if goal line is already active

        // --- Calculate Parameters Programmatically ---
        const firstResult = group[0];
        const catId = firstResult?.CategoryID;
        const levelId = firstResult?.LevelID;

        let skill = "";
        try {
          if (firstResult?.ResultRef) {
            const parsed =
              typeof firstResult.ResultRef === "string"
                ? JSON.parse(firstResult.ResultRef)
                : firstResult.ResultRef;
            skill = parsed?.AssessmentSkill || parsed?.Skill || "";
          }
        } catch (err) {}
        skill =
          skill || firstResult?.AssessmentSkill || firstResult?.SkillType || "";

        // Target Score extraction matching End of Year metrics
        let targetScore = "";
        if (catId === 3 || catId === 4) {
          const matchedSkillKey =
            Object.keys(ACADIENCE_EOY_TARGETS[catId] || {}).find((k) =>
              skill.toUpperCase().includes(k.toUpperCase()),
            ) || (catId === 4 ? "%" : "CLS");
          targetScore =
            ACADIENCE_EOY_TARGETS[catId]?.[matchedSkillKey]?.[levelId] || "";
        } else {
          const assessmentName = firstResult?.AssessmentType || "";
          const fallbackKey = Object.keys(ACADIENCE_EOY_TARGETS).find((k) =>
            assessmentName.toLowerCase().includes(k.toLowerCase()),
          );
          targetScore = ACADIENCE_EOY_TARGETS[fallbackKey]?.[levelId] || "";
        }

        // Resolve Academic School Boundaries
        let academicStart =
          schoolYears?.current?.StartDate || schoolYears?.StartDate;
        let academicEnd = schoolYears?.current?.EndDate || schoolYears?.EndDate;

        if (!academicStart || !academicEnd) {
          const maxDate = new Date();
          const startYear =
            maxDate.getMonth() >= 7
              ? maxDate.getFullYear()
              : maxDate.getFullYear() - 1;
          academicStart = `${startYear}-08-01`;
          academicEnd = `${startYear + 1}-07-31`;
        }

        // Collect all assessment result unique identifiers linked to this chart group
        const selectedAssessmentResultsID = group
          .map((d) => d.ResultID)
          .filter(Boolean);

        const silentSubmission = {
          goalDesc: firstResult?.AssessmentType
            ? `${firstResult.AssessmentType} EOY Goal`
            : "Automated Goal Line",
          startDate: toDateLocal(academicStart),
          endDate: toDateLocal(academicEnd),
          baseScore: firstResult?.TotalScore ?? 0,
          targetScore: targetScore || 100, // Safe benchmark fallback if unmapped
          resultID: selectedAssessmentResultsID,
        };

        try {
          // Call your Redux action API to populate database rows
          await addPMGoal(silentSubmission);
          structureUpdated = true;
        } catch (err) {
          console.error(
            `Failed to auto-provision goal line for group ${key}:`,
            err,
          );
        }
      }

      // Refresh layout store once after provisioning mutations complete
      if (structureUpdated && selectedUser?.UserID) {
        await loadPMResultsList(selectedUser.UserID);
      }
    };

    provisionMissingGoals();
    // Safe layout dependencies to handle data loads cleanly without infinite loops
  }, [groupedData, pmresultsListLoading, schoolYears, selectedUser?.UserID]);

  // keep a single form state for Add and Edit: initialize when modal opens
  useEffect(() => {
    if (!goalSettings) return;

    if (goalExisting && goalExisting.GoalID) {
      setGoalForm({
        goalDesc: goalExisting.GoalDesc || "",
        startDate: goalExisting.StartDate || "",
        endDate: goalExisting.EndDate || "",
        baseScore: goalExisting.BaseScore ?? "",
        targetScore: goalExisting.TargetScore ?? "",
      });
    } else {
      // === NEW GOAL HARDCODED MAPPING FROM TARGET SHEET ===
      const group =
        goalSettings?.data || groupedData[goalSettings?.groupKey] || [];
      const firstResult = group[0] || null;

      const catId = firstResult?.CategoryID;
      const levelId = firstResult?.LevelID;

      // Parse out sub-skill strings safely
      let skill = "";
      try {
        if (firstResult?.ResultRef) {
          const parsed =
            typeof firstResult.ResultRef === "string"
              ? JSON.parse(firstResult.ResultRef)
              : firstResult.ResultRef;
          skill = parsed?.AssessmentSkill || parsed?.Skill || "";
        }
      } catch (err) {}
      skill =
        skill || firstResult?.AssessmentSkill || firstResult?.SkillType || "";

      // Resolve target mapping based on document structures
      let targetScore = "";
      if (catId === 3 || catId === 4) {
        // Handles Silly Words (CLS/WWR) and Story Reading (%/TWC)
        const matchedSkillKey =
          Object.keys(ACADIENCE_EOY_TARGETS[catId] || {}).find((k) =>
            skill.toUpperCase().includes(k.toUpperCase()),
          ) || (catId === 4 ? "%" : "CLS"); // safe standard defaults if ambiguous

        targetScore =
          ACADIENCE_EOY_TARGETS[catId]?.[matchedSkillKey]?.[levelId] || "";
      } else {
        // Handles general letter subskills
        const assessmentName = firstResult?.AssessmentType || "";
        const fallbackKey = Object.keys(ACADIENCE_EOY_TARGETS).find((k) =>
          assessmentName.toLowerCase().includes(k.toLowerCase()),
        );
        targetScore = ACADIENCE_EOY_TARGETS[fallbackKey]?.[levelId] || "";
      }

      // Extract Academic year dates from schoolYears profile
      let academicStart =
        schoolYears?.current?.StartDate || schoolYears?.StartDate;
      let academicEnd = schoolYears?.current?.EndDate || schoolYears?.EndDate;

      // Fixed fallback context to mimic your global layout window range
      if (!academicStart || !academicEnd) {
        const maxDate = new Date();
        const startYear =
          maxDate.getMonth() >= 7
            ? maxDate.getFullYear()
            : maxDate.getFullYear() - 1;
        academicStart = `${startYear}-08-01`;
        academicEnd = `${startYear + 1}-07-31`;
      }

      setGoalForm({
        goalDesc: firstResult?.AssessmentType
          ? `${firstResult.AssessmentType} EOY Goal`
          : "",
        startDate: toDateLocal(academicStart),
        endDate: toDateLocal(academicEnd),
        baseScore: firstResult?.TotalScore ?? "",
        targetScore: targetScore,
      });
    }
  }, [goalSettings, goalExisting, schoolYears]); // Zero object references; infinite loop completely eliminated

  // keep a single form state for Add and Edit: initialize when modal opens
  // useEffect(() => {
  //   if (!goalSettings) return;

  //   if (goalExisting && goalExisting.GoalID) {
  //     setGoalForm({
  //       goalDesc: goalExisting.GoalDesc || "",
  //       startDate: goalExisting.StartDate || "",
  //       endDate: goalExisting.EndDate || "",
  //       baseScore: goalExisting.BaseScore ?? "",
  //       targetScore: goalExisting.TargetScore ?? "",
  //     });
  //   } else {
  //     // === NEW GOAL HARDCODED MAPPING ===
  //     const group = goalSettings?.data || groupedData[goalSettings?.groupKey] || [];
  //     const firstResult = group[0] || null;
  //     console.log("Initializing new goal form with first result", firstResult);

  //     // 1. Get the current active level from the data group
  //     const currentLevel = firstResult?.LevelID;

  //     // 2. Map the hardcoded target score based on the level
  //     const targetScore = LEVEL_TARGET_SCORES[currentLevel] ?? "";

  //     // 3. Extract dates directly from academic school years props, or fall back to standard session limits
  //     let academicStart = schoolYears?.current?.StartDate || schoolYears?.StartDate;
  //     let academicEnd = schoolYears?.current?.EndDate || schoolYears?.EndDate;

  //     // Fallback if schoolYears object isn't populated or structured this way
  //     if (!academicStart || !academicEnd) {
  //       const currentYear = new Date().getFullYear();
  //       const currentMonth = new Date().getMonth(); // 0 = Jan, 7 = Aug

  //       // If we are before August, the academic year started last year
  //       const startYear = currentMonth >= 7 ? currentYear : currentYear - 1;

  //       academicStart = `${startYear}-08-01`;     // August 1st
  //       academicEnd = `${startYear + 1}-07-31`;   // July 31st next year
  //     }

  //     setGoalForm({
  //       goalDesc: firstResult?.AssessmentType ? `${firstResult.AssessmentType} Goal` : "",
  //       startDate: toDateLocal(academicStart),
  //       endDate: toDateLocal(academicEnd),
  //       baseScore: firstResult?.TotalScore ?? "", // Base score remains the first total score
  //       targetScore: targetScore,                 // Injected hardcoded target score
  //     });
  //   }
  // }, [goalSettings, goalExisting]);

  // clear form when modal is closed
  useEffect(() => {
    if (goalSettings) return;
    setGoalForm({
      goalDesc: "",
      startDate: "",
      endDate: "",
      baseScore: "",
      targetScore: "",
    });
  }, [goalSettings]);

  // Always fetch fresh PM results when a student is selected or component mounts
  useEffect(() => {
    if (loadPMResultsList && selectedUser && selectedUser.UserID) {
      loadPMResultsList(selectedUser.UserID);
    }
  }, [loadPMResultsList, selectedUser?.UserID]);

  return (
    <>
      {pmresultsListLoading ? (
        <div>Loading...</div>
      ) : (
        <div className="container-fluid w-100 py-3">
          <div className="row g-3">
            {(() => {
              const keys = groupedData && Object.keys(groupedData);
              if (keys && keys.length > 0) {
                return keys.map((key) => (
                  <div className="col-12 col-md-6 col-xl-6" key={key}>
                    <div className="h-100 border rounded p-2 bg-white">
                      <PMChart
                        data={groupedData[key]}
                        setGoalSettings={setGoalSettings}
                        groupKey={key}
                        globalChartDomain={globalChartDomain}
                      />
                    </div>
                  </div>
                ));
              }
            })()}
          </div>
        </div>
      )}

      {/* Goal Settings Modal */}
      <Modal show={!!goalSettings} onHide={() => setGoalSettings(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {goalExisting && goalExisting.GoalID ? "Edit" : "Add"} Goal Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Goal Description</Form.Label>
              <Form.Control
                type="text"
                value={goalForm.goalDesc}
                onChange={(e) => handleGoalChange("goalDesc", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <DatePicker
                label="Start Date"
                type="date"
                value={goalForm.startDate}
                onChange={(value) => handleGoalChange("startDate", value)}
                useBootstrap={true}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <DatePicker
                label="End Date"
                type="date"
                value={goalForm.endDate}
                onChange={(value) => handleGoalChange("endDate", value)}
                useBootstrap={true}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Base Score</Form.Label>
              <Form.Control
                type="number"
                readOnly // ← add this
                className="bg-light text-muted" // ← visual cue it's locked
                value={goalForm.baseScore}
                onChange={(e) => handleGoalChange("baseScore", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Target Score</Form.Label>
              <Form.Control
                type="number"
                value={goalForm.targetScore}
                useBootstrap={true}
                // readOnly                          // ← add this
                // className="bg-light text-muted" // ← visual cue it's locked
                onChange={(e) =>
                  handleGoalChange("targetScore", e.target.value)
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setGoalSettings(null)}>
            Cancel
          </Button>
          <Button className="button-primary" onClick={handleSaveGoal}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

PMDashboard.propTypes = {
  pmresultsList: PropTypes.array,
  loadPMResultsList: PropTypes.func,
  addPMGoal: PropTypes.func,
  updatePMGoal: PropTypes.func,
  pmresultsListLoading: PropTypes.bool,
  clearAll: PropTypes.func,
  user: PropTypes.object,
  schoolYears: PropTypes.object,
};

PMDashboard.defaultProps = {
  pmresultsList: [],
  pmresultsListLoading: false,
  loadPMResultsList: null,
  addPMGoal: null,
  clearAll: null,
};
const mapStateToProps = (state) => ({
  pmresultsList: state.admin.pmresultsList,
  pmresultsListLoading: state.admin.pmresultsListLoading,
  user: state.auth.user,
  selectedUser: state.admin.user,
  schoolYears: state.admin.schoolYears,
});

export default connect(mapStateToProps, {
  loadPMResultsList,
  addPMGoal,
  updatePMGoal,
})(PMDashboard);
